import React, { useCallback } from 'react';
import { sprintf } from '@splunk/ui-utils/format';
import { _ } from '@splunk/ui-utils/i18n';
import SquaresLayered from '@splunk/react-icons/SquaresLayered';
import Button, { type ButtonPropsBase } from '@splunk/react-ui/Button';
import { useUserMessageAPI } from '@splunk/dashboard-context';
import { Clipboard, console as consoleClass } from '@splunk/dashboard-utils';

const copyIcon = <SquaresLayered />;

export type CopyButtonProps = {
    value: string;
    successMessage?: string;
    errorMessage?: string;
    disableConfirmation?: boolean;
    title?: string;
} & Omit<ButtonPropsBase, 'children' | 'onClick'>;

const getErrorMessage = (err: unknown) => {
    if (!globalThis.isSecureContext) {
        return _('Copy functionality requires an HTTPS connection');
    }

    return err instanceof Error ? err.message : _('Unknown error');
};

export const CopyButton = ({
    value,
    successMessage = _('Copied to clipboard'),
    errorMessage = _('Failed to copy to clipboard: %(errorMessage)s'),
    disableConfirmation,
    title = _('Copy'),
    ...buttonProps
}: CopyButtonProps) => {
    const userMessageApi = useUserMessageAPI();

    const handleClick = useCallback(async () => {
        try {
            await Clipboard.writeText(value);

            if (!disableConfirmation && userMessageApi) {
                userMessageApi({ message: successMessage, level: 'success' });
            }
        } catch (err) {
            const message = getErrorMessage(err);
            const formattedMessage = sprintf(errorMessage, {
                errorMessage: message,
            });

            consoleClass.error(formattedMessage);

            if (!disableConfirmation && userMessageApi) {
                userMessageApi({ message: formattedMessage, level: 'error' });
            }
        }
    }, [
        disableConfirmation,
        errorMessage,
        successMessage,
        userMessageApi,
        value,
    ]);

    return (
        <Button
            icon={copyIcon}
            appearance="secondary"
            data-test="copy-button"
            title={title}
            {...buttonProps}
            onClick={handleClick}
        />
    );
};
